import { parse, toSeconds } from "iso8601-duration";

import type { TimeValue } from "../components/TimeInput";

/**
 * Parses an ISO 8601 duration string to total minutes.
 * Uses the iso8601-duration library which supports the full ISO 8601 spec
 * including years, months, weeks, days, hours, minutes, and seconds.
 *
 * @param iso - ISO 8601 duration string (e.g. "PT8H30M", "P1D", "P1Y2M3DT4H5M6S")
 * @returns Total minutes represented by the duration, or 0 if invalid/empty
 *
 * @example
 * parseIso8601Duration("PT8H30M")   // 510
 * parseIso8601Duration("PT24H")     // 1440
 * parseIso8601Duration("P1D")       // 1440
 * parseIso8601Duration("P1DT2H30M") // 1590
 * parseIso8601Duration("")           // 0
 */
export function parseIso8601Duration(iso: string): number {
	if (!iso) return 0;
	try {
		const isNegative = iso.startsWith("-");
		const normalized = isNegative ? iso.slice(1) : iso;
		const totalMinutes = Math.ceil(toSeconds(parse(normalized)) / 60);
		return isNegative ? -totalMinutes : totalMinutes;
	} catch {
		return 0;
	}
}

/**
 * Parses an ISO 8601 duration string to a TimeValue ({ hour, minute }).
 *
 * @param iso - ISO 8601 duration string (e.g. "PT8H30M", "P1D")
 * @returns TimeValue with hour and minute, or { hour: null, minute: null } for empty input
 *
 * @example
 * parseIso8601ToTimeValue("PT8H30M")  // { hour: 8, minute: 30 }
 * parseIso8601ToTimeValue("P1D")      // { hour: 24, minute: 0 }
 * parseIso8601ToTimeValue("")          // { hour: null, minute: null }
 */
export function parseIso8601ToTimeValue(iso: string): TimeValue {
	if (!iso) return { hour: null, minute: null };
	const totalMinutes = parseIso8601Duration(iso);
	return { hour: Math.floor(totalMinutes / 60), minute: totalMinutes % 60 };
}

/**
 * Converts a TimeValue ({ hour, minute }) to an ISO 8601 duration string.
 *
 * @param value - TimeValue with hour and minute (null values treated as 0)
 * @returns ISO 8601 duration string (e.g. "PT8H30M")
 *
 * @example
 * timeValueToIso8601({ hour: 8, minute: 30 })    // "PT8H30M"
 * timeValueToIso8601({ hour: 24, minute: 0 })     // "PT24H0M"
 * timeValueToIso8601({ hour: null, minute: null }) // "PT0H0M"
 */
export function timeValueToIso8601(value: TimeValue): string {
	const h = value.hour ?? 0;
	const m = value.minute ?? 0;
	return `PT${h}H${m}M`;
}

/**
 * Formats an ISO 8601 duration string to "HH:mm" display format.
 * Always displays total hours (e.g. "P1D" → "24:00", not "1 day").
 *
 * @param iso - ISO 8601 duration string (e.g. "PT8H30M", "P1D")
 * @returns Formatted string in "HH:mm" format
 *
 * @example
 * formatDurationToHHmm("PT8H30M")  // "08:30"
 * formatDurationToHHmm("P1D")      // "24:00"
 * formatDurationToHHmm(null)        // "00:00"
 */
export function formatDurationToHHmm(iso: string | null | undefined): string {
	if (!iso) return "00:00";
	const totalMinutes = parseIso8601Duration(iso);
	const hours = Math.floor(Math.abs(totalMinutes) / 60);
	const minutes = Math.abs(totalMinutes) % 60;
	const pad = (n: number) => String(n).padStart(2, "0");
	return `${pad(hours)}:${pad(minutes)}`;
}
